import { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { Button } from "../components";
import Logo from "../assets/images/Logo.svg";
import { colors } from "../theme/colors";
import useGeolocation from "../hooks/useGeoLocation";
import { login, setUserDetails } from "../store/authReducer";
import { RootState, useAppSelector } from "../store";
import { appConfig } from "../hooks/useConfig";
import { API_URL } from "../services/constants";

// Props for the BasicLayout component
interface Props {
  window?: () => Window;
}

// Navigation items with links, IDs, and hrefs
const navItems = [
  {
    link: "Services",
    id: "services",
    href: "/",
  },
  {
    link: "Technology",
    id: "technology",
    href: "/technology",
  },
  {
    link: "Contact Us",
    id: "contact",
    href: "/contact",
  },
  {
    link: "About Us",
    id: "about",
    href: "/aboutus",
  },
];

// Main AuthLayout component
const AuthLayout = (props: Props) => {
  const navigate = useNavigate();
  const {
    getAccessTokenSilently,
    loginWithRedirect,
    handleRedirectCallback,
    user,
    isAuthenticated,
    logout,
    error
  } = useAuth0();
  const dispatch = useDispatch()
  const { location, fetchLocation } = useGeolocation();

  // State to track the active route index
  const [activeRoute, setActiveRoute] = useState(0);
  const [userRole, setUserRole] = useState("");
  const [userMail, setUserMail] = useState("");
  const storedUserRole = useAppSelector(
    (state: RootState) => state.auth.userRole
  );

  // Function to handle navigation item click
  const handleClick = (link: string) => {
    navigate(link);
  };

  const handleLogin = async () => {
    await fetchLocation();
  };

  useEffect(() => {
    if (location) {
      const userLocation = btoa(
        JSON.stringify({
          latitude: location.latitude,
          longitude: location.longitude,
          // latitude: 34.05400306917684,
          // longitude: -118.2437,
        })
      );
      const authParams = {
        redirect_uri: window.location.origin,
        scope: "offline_access profile email read:roles",
        audience: "PegasusAdminPanel",
        location_data: userLocation,
        device_token: uuidv4(),
      };

      loginWithRedirect({
        authorizationParams: {
          ...authParams,
        },
      });
    }
  }, [location, loginWithRedirect]);

  useEffect(() => {
    const checkRedirect = async () => {
      // Only proceed if we have an authorization code in the URL
      const searchParams = new URLSearchParams(window.location.search);
      const hasCode = searchParams.has("code");
      const hasState = searchParams.has("state");
      const hasError = searchParams.has("error");

      if (hasError) {
        // const error = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");
        window.alert(
          "Authentication error: " + (errorDescription || "Unknown error")
        );
        // Scrub ?error=&error_description= from the URL/history before leaving —
        // error_description can carry partial auth context (blocked IP, geo
        // restriction) and must not linger in the back stack. Matches the
        // success and setCookies-failure paths.
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        await logout();
        return;
      }

      if (!hasCode || !hasState) {
        return; // No auth flow in progress
      }

      try {
        const { appState } = await handleRedirectCallback();

        const accessToken = await getAccessTokenSilently();
        const decodedToken: any = jwtDecode(accessToken);
        const userRole = decodedToken["https://pegasus.user-info/"].role;
        const emailFromToken = decodedToken?.["https://pegasus.user-info/"]?.mail;
        const healthOrgId = decodedToken?.["https://pegasus.user-info/"]?.health_org_id ?? 0;
        const laboratoryId = decodedToken?.["https://pegasus.user-info/"]?.laboratory_id ?? 0;
        const userMail = emailFromToken || user?.email || "";
        setUserMail(userMail);
        setUserRole(userRole[0]);
        dispatch(login({ userRole: userRole[0], accessToken: accessToken, refreshToken: "", userMail, }))
        dispatch(setUserDetails({ userDetails: { health_org_id: healthOrgId ?? 0, laboratory_id: laboratoryId ?? 0 } }));

        const response = await fetch(
          `${appConfig?.API_BASE_URL}${API_URL.setCookies}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to set cookies");
        }

        // Clean the URL after successful auth
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (err) {
        // Clean the URL even if there was an error
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    };

    checkRedirect();
  }, [handleRedirectCallback, getAccessTokenSilently]);

  useEffect(() => {
    const effectiveRole = userRole || storedUserRole;
    if (!isAuthenticated || !effectiveRole) return;
    navigate(
      effectiveRole === "OT_ADMIN" ||
        effectiveRole === "HEALTH_ORG_ADMIN" ||
        effectiveRole === "LABORATORY_ADMIN"
        ? "/specimens"
        : "/home",
      {
        replace: true,
      }
    );
  }, [isAuthenticated, navigate, userRole, storedUserRole]);

  return (
    // Main layout structure using Material-UI components
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      <CssBaseline />
      <AppBar component="nav">
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
            columnGap: "35px",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center" }}
          >
            <img src={Logo} alt="Logo" width={100} height={70} />
            <Box>
              {navItems.map((item, i) => {
                const active = activeRoute === i;
                return (
                  <Button
                    variant="text"
                    key={i}
                    id={item.id}
                    onClick={() => {
                      handleClick(item.href);
                      setActiveRoute(i);
                    }}
                    disableRipple
                    sx={{
                      color: "primary.light",
                      fontSize: 16,
                      fontWeight: active ? "900" : "600",
                      textDecoration: active ? "underline" : "none",
                      width: "120px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    label={item.link}
                  />
                );
              })}
            </Box>
          </Box>
          <Box>
            <Button
              variant="text"
              onClick={() => {
                navigate("/forgotpassword");
              }}
              disableRipple
              sx={{
                color: "primary.light",
                fontSize: 16,
                fontWeight: "600",
                textDecoration: "none",
                width: "200px",
                ":hover": {
                  backgroundColor: colors.primary,
                  fontWeight: "700",
                  textDecoration: "underline",
                },
              }}
              label={"Forgot Password?"}
            />
            <Button
              onClick={async () => {
                await handleLogin();
                // await logout()
              }}
              sx={{
                backgroundColor: colors.darkRed,
                borderRadius: "10px",
                width: "141px",
                height: "64px",
                color: "primary.light",
                fontSize: "16px",
                fontWeight: "700",
                ":hover": {
                  backgroundColor: colors.darkerRed,
                  fontWeight: "900",
                },
              }}
              label={"Login"}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Box pt="97px" width="100%" flex={1}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default AuthLayout;